import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Clock, Store, Sparkles, ArrowLeft, PauseCircle, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { formatHours, isOpenNow } from '@/hooks/useOpeningHours';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DEFAULTS = {
  storefront: { open_time: '09:00', close_time: '18:00', open_days: [1, 2, 3, 4, 5, 6] },
  experience: { open_time: '09:00', close_time: '20:00', open_days: [0, 1, 2, 3, 4, 5, 6] },
};

function ScheduleCard({ title, icon: Icon, description, record, saving, onSave }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (form !== null) return;
    if (record) {
      setForm({
        open_time: record.open_time || DEFAULTS[title === 'Storefront Hours' ? 'storefront' : 'experience'].open_time,
        close_time: record.close_time || DEFAULTS[title === 'Storefront Hours' ? 'storefront' : 'experience'].close_time,
        open_days: record.open_days || [],
      });
    } else {
      setForm({ ...DEFAULTS[title === 'Storefront Hours' ? 'storefront' : 'experience'] });
    }
  }, [record, form, title]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      open_days: f.open_days.includes(d)
        ? f.open_days.filter((x) => x !== d)
        : [...f.open_days, d].sort((a, b) => a - b),
    }));

  const preview = formatHours(form);
  const open = isOpenNow(form);
  const field = 'bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60';

  return (
    <div className="bg-[#111115] border border-white/5 p-6 space-y-5 max-w-2xl">
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-primary" />
        <div>
          <p className="text-white font-bold text-sm">{title}</p>
          <p className="text-white/40 text-xs">{description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Opening Time</Label>
          <Input type="time" value={form.open_time} onChange={(e) => set('open_time', e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Closing Time</Label>
          <Input type="time" value={form.close_time} onChange={(e) => set('close_time', e.target.value)} className={field} />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Open Days</Label>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleDay(i)}
              className={`px-4 py-2 text-xs font-bold tracking-wider border transition-all ${
                form.open_days.includes(i)
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white/5 border-white/10 text-white/40 hover:border-primary/40'
              }`}
            >
              {d.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/5 pt-4">
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">PREVIEW</p>
          <p className="text-white/80 text-sm">{preview}</p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-2.5 py-1 border ${
            open
              ? 'text-green-400 border-green-400/30 bg-green-400/10'
              : 'text-white/40 border-white/10 bg-white/5'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-green-400' : 'bg-white/30'}`} />
          {open ? 'OPEN NOW' : 'CLOSED NOW'}
        </span>
      </div>

      <button
        onClick={() => onSave(form)}
        disabled={saving}
        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-5 py-3 transition-all disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE {title.toUpperCase()}
      </button>
    </div>
  );
}

export default function AdminOpeningHours() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [savingKey, setSavingKey] = useState('');

  const { data: hours = [], isLoading } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => base44.entities.OpeningHours.list(),
  });

  const { data: venue } = useQuery({
    queryKey: ['venue-settings'],
    queryFn: () => base44.entities.VenueSettings.list(),
  });
  const venueSettings = venue?.[0] || null;

  const byKey = {};
  for (const r of hours) {
    if (r.schedule_key) byKey[r.schedule_key] = r;
  }

  const togglePause = async () => {
    if (!venueSettings) {
      toast({ title: 'Save your venue settings first (capacity required)', variant: 'destructive' });
      return;
    }
    try {
      await base44.entities.VenueSettings.update(venueSettings.id, { bookings_paused: !venueSettings.bookings_paused });
      queryClient.invalidateQueries({ queryKey: ['venue-settings'] });
      toast({ title: venueSettings.bookings_paused ? 'Bookings resumed' : 'Bookings paused — calendar shows fully booked' });
    } catch (e) {
      toast({ title: 'Failed to toggle', variant: 'destructive' });
    }
  };

  const save = async (key, form) => {
    setSavingKey(key);
    try {
      const existing = byKey[key];
      const payload = { schedule_key: key, ...form };
      if (existing) {
        await base44.entities.OpeningHours.update(existing.id, payload);
      } else {
        await base44.entities.OpeningHours.create(payload);
      }
      queryClient.invalidateQueries({ queryKey: ['opening-hours'] });
      toast({ title: `${key === 'storefront' ? 'Storefront' : 'Experience'} hours saved` });
    } catch (e) {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
    setSavingKey('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">CONFIGURATION</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Opening Hours</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Set the hours shown across the shop and customer service pages, and the hours that drive bookable session slots. Each schedule uses one daily open/close window applied to the days you select.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto space-y-8">
        {/* Bookings pause toggle */}
        <div className={`border p-6 max-w-2xl ${venueSettings?.bookings_paused ? 'border-primary/40 bg-primary/[0.06]' : 'border-white/5 bg-[#111115]'}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              {venueSettings?.bookings_paused
                ? <PauseCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                : <PlayCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />}
              <div>
                <p className="text-white font-bold text-sm">Experience Bookings</p>
                <p className="text-white/40 text-xs mt-1 leading-relaxed">
                  When paused, the public booking calendar shows every slot as fully booked.
                  Customers can still join a waitlist for cancellations — you'll see entries in the Waitlist admin.
                </p>
              </div>
            </div>
            <button
              onClick={togglePause}
              disabled={!venueSettings}
              className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full border transition-colors disabled:opacity-40 ${
                venueSettings?.bookings_paused ? 'bg-primary border-primary' : 'bg-white/10 border-white/15'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${venueSettings?.bookings_paused ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {venueSettings?.bookings_paused && (
            <div className="mt-4 border-t border-primary/20 pt-3">
              <p className="text-primary text-[11px] font-bold tracking-widest">⚠ BOOKINGS CURRENTLY PAUSED</p>
              <p className="text-white/40 text-xs mt-1">Toggle off when ready to go live.</p>
            </div>
          )}
        </div>

        <ScheduleCard
          title="Storefront Hours"
          icon={Store}
          description="Shown on the Home, Shop, Contact and Customer Service pages."
          record={byKey.storefront}
          saving={savingKey === 'storefront'}
          onSave={(form) => save('storefront', form)}
        />
        <ScheduleCard
          title="Experience Hours"
          icon={Sparkles}
          description="Controls which days and times are bookable on the booking flow."
          record={byKey.experience}
          saving={savingKey === 'experience'}
          onSave={(form) => save('experience', form)}
        />
        <p className="text-white/30 text-xs flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-primary" />
          Capacity, slot duration and turnaround buffer are still managed under Venue Management.
        </p>
      </section>
    </div>
  );
}